// The motif of the puzzle on the board: the motif of the mistake it was made
// from, read through the `mistakes.motifs` service so the card, the banner
// and the Learn card name the same thing the Blunders and Misses screens
// do. Pure: the container hands in what it has and gets the motif back.
//
// What the classifier reads (`MotifInput`) is the mistake's fact. The fact
// comes from the store when the game is in the last 30 days
// (`GameFacts.mistakes`, the fact at the mistake's 1-based ply); for an
// older game, whose facts the store does not hold, the same input is built
// from the review's own scan and game the way `facts/mistakes.ts` of
// `@chess/api-insights` builds the fact: the positions before and after the
// move, the engine's best line before and its reply after, and the material
// each wins (`lostPieceOf`). The review's verdict on the move
// (`GameScan.kind`) is the fact's `kind`.
//
// `createMotifReader` holds one motif per game and ply, so the three cards
// classify a puzzle once and never in render; a new game drops the held
// ones.
import type { Color, Fen, MistakeFact, Move, MoveKind, PositionAnalysis } from '@chess/api-model';
import { familyLabel, variationLabel, type Motif, type MotifInput, type Motifs } from '@chess/api-mistakes';
import { lostPieceOf } from '@chess/api-insights';
import type { Puzzle } from './finder.ts';

/** What the motif reads of the review's scan: the `GameScan` of `@chess/api-review` satisfies it. */
export interface MotifScan {
  /** Blunder, miss or neither for the move at a 1-based ply, played by `mover`. */
  kind(ply: number, move: Move | undefined, mover: Color): MoveKind | undefined;
  /** The engine's reading of the position after `ply` moves. */
  position(ply: number): PositionAnalysis | undefined;
}

/** What the container has at hand for the open game. */
export interface MotifSource {
  /** The game's facts from the store, when the game is in the period. */
  facts?: readonly MistakeFact[];
  scan?: MotifScan;
  game?: { moves: readonly Move[]; positions: readonly Fen[] };
  reader?: Color;
}

const other = (c: Color): Color => (c === 'white' ? 'black' : 'white');

/** The 1-based ply of the reader's move a puzzle is about; none for the opening's theory puzzle. */
export function mistakePlyOf(puzzle: Pick<Puzzle, 'kind' | 'ply'>): number | undefined {
  return puzzle.kind === 'theory' ? undefined : puzzle.ply + 1;
}

/**
 * The classifier's input for the reader's move at a 1-based ply: the
 * store's fact when there is one, else one built from the scan; undefined
 * when the move is not a blunder or a miss, or the scan has not read the
 * positions around it.
 */
export function motifInputAt(ply: number, source: MotifSource): MotifInput | undefined {
  const { reader } = source;
  if (!reader) return undefined;
  const fact = source.facts?.find((m) => m.ply === ply);
  if (fact) return { ...fact, reader };
  const { scan, game } = source;
  if (!scan || !game) return undefined;
  const move = game.moves[ply - 1];
  const kind = scan.kind(ply, move, reader);
  const fenBefore = game.positions[ply - 1];
  const fenAfter = game.positions[ply];
  if (!kind || !fenBefore || !fenAfter) return undefined;
  const before = scan.position(ply - 1);
  const after = scan.position(ply);
  if (!before || !after) return undefined;
  const best = before.lines[0];
  const reply = after.lines[0];
  return {
    kind,
    reader,
    fenBefore,
    fenAfter,
    best: best ? { pv: best.pv, mate: best.score.mate } : undefined,
    reply: reply ? { pv: reply.pv, mate: reply.score.mate } : undefined,
    lost: kind === 'blunder' ? lostPieceOf(fenAfter, reply, after.score, reader) : undefined,
    missed: kind === 'miss' ? lostPieceOf(fenBefore, best, best?.score, other(reader)) : undefined,
  };
}

/** The motif as the card's and banner's lines say it: "Pin, against the queen", the family alone without a variation. */
export function motifPhrase(motif: Motif): string {
  const family = familyLabel(motif.family);
  const variation = motif.variation ? variationLabel(motif.family, motif.variation) : undefined;
  return variation ? `${family}, ${variation}` : family;
}

/** The motif of a puzzle of a game, held once read. */
export interface MotifReader {
  (gameId: string, puzzle: Pick<Puzzle, 'kind' | 'ply'>, source: MotifSource): Motif | undefined;
}

/**
 * A reader over the service that classifies each puzzle of a game once: the
 * motif is held by game and ply and handed back on every later read, so
 * the cards never classify in render. A read that finds no input (the scan
 * not there yet) holds nothing and is tried again. Another game empties
 * what is held.
 */
export function createMotifReader(motifs: Pick<Motifs, 'classify'>): MotifReader {
  let heldFor: string | undefined;
  const held = new Map<number, Motif>();
  return (gameId, puzzle, source) => {
    const ply = mistakePlyOf(puzzle);
    if (ply === undefined) return undefined;
    if (heldFor !== gameId) {
      held.clear();
      heldFor = gameId;
    }
    const known = held.get(ply);
    if (known) return known;
    const input = motifInputAt(ply, source);
    if (!input) return undefined;
    const motif = motifs.classify(input);
    held.set(ply, motif);
    return motif;
  };
}
